import React, { Fragment, PureComponent } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { colors } from '../../../constants/colors';
import { fontFamily, fontSize } from '../../../constants/text';
import { getAcceptedInsurances, isSelfPayOnly } from '../../../core/util/location';
import { isEmptyArray } from '../../../core/util/array';
import PanelCard from '../PanelCards/components/PanelCard';
import { smallerThanOrEqualTo } from '../../../core/util/styledComponents';
import { isEmpty } from '../../../core/util/empty';
import Collapse from '@material-ui/core/Collapse/Collapse';
import { getCustomization, INSURANCE_AND_PAYMENTS } from '../util/customization';

const Root = styled.div`
  -webkit-font-smoothing: antialiased;
`;

const Copy = styled.div`
  color: ${colors.blueMonday};
  font-size: ${fontSize.mediumSmall};
  font-family: ${fontFamily.book};
  padding: 0 0 30px;
  ${smallerThanOrEqualTo.md`
    padding: 0;
  `};
`;

const InsuranceIcon = styled.img`
  width: 15px;
  height: 15px;
  padding: 2px 10px 0 0;
  display: inline-block;
  vertical-align: top;
`;

const Accepted = styled.div`
  display: flex;
  ${smallerThanOrEqualTo.md`
      padding-top: 30px;
  `};
`;

const AcceptedHeader = styled.div`
  font-family: ${fontFamily.bold};
  font-size: ${fontSize.default};
  color: ${colors.squid};
  padding-bottom: 10px;
`;

const AcceptedInsurances = styled.div`
  font-family: ${fontFamily.book};
  font-size: ${fontSize.mediumSmall};
  color: ${colors.squid};
`;

const AcceptedPayments = styled.div`
  font-family: ${fontFamily.book};
  font-size: ${fontSize.mediumSmall};
  color: ${colors.squid};
`;

const InsuranceType = styled.span`
  &:after {
    content: ', ';
  }

  &:last-of-type {
    &:after {
      content: '';
    }
  }
`;

const PaymentType = styled.span`
  &:after {
    content: ', ';
  }

  &:last-of-type {
    &:after {
      content: '';
    }
  }
`;

const AcceptedContainer = styled.div`
  display: flex;
  justify-content: space-between;
  ${smallerThanOrEqualTo.md`
    display: block;
  `};
`;

const AcceptedInfo = styled.div`
  display: block;
`;

const ViewAll = styled.div`
  border: 0;
  color: ${colors.blueJayWay};
  font-size: ${fontSize.mediumSmall};
  margin: 10px 0;
  cursor: pointer;
  background-color: transparent;
`;

const MAX_NOT_COLLAPSED_INSURANCES = 5;

export default class InsuranceAndPayments extends PureComponent {
  static propTypes = {
    className: PropTypes.string,
    location: PropTypes.object,
    enableCollapsing: PropTypes.bool,
    defaultExpanded: PropTypes.bool,
  };

  constructor(props) {
    super(props);
    this.state = { insuranceSectionCollapsed: false };
    const customization = getCustomization(INSURANCE_AND_PAYMENTS, {
      location: this.props.location,
    });
    this.isInsuranceSectionCollapsable = customization.insuranceSectionCollapsable;
    this.entity = customization.entity;
    this.insurances = getAcceptedInsurances(this.props.location);
  }

  shouldRender = (insurances, acceptedPayments) =>
    !isEmpty(insurances) || !isEmpty(acceptedPayments);

  toggleViewAllInsuranceProviders = () => {
    this.setState({
      insuranceSectionCollapsed: !this.state.insuranceSectionCollapsed,
    });
  };

  shouldShowViewAllCta = () =>
    this.isInsuranceSectionCollapsable && this.insurances.length > MAX_NOT_COLLAPSED_INSURANCES;

  render = () => {
    const { location, className, enableCollapsing, defaultExpanded } = this.props;
    const { insuranceSectionCollapsed } = this.state;
    const acceptedPayments = location.acceptedPaymentMethods;
    if (!this.shouldRender(this.insurances, acceptedPayments)) return null;
    const collapsedInsurances = this.insurances.slice(0, MAX_NOT_COLLAPSED_INSURANCES);

    return (
      <PanelCard title="Insurance & Payments" {...{ enableCollapsing, defaultExpanded }}>
        <Root className={className}>
          <Copy>
            {isSelfPayOnly(location) ? (
              <Fragment>
                {location.displayNamePrimary} does not accept insurances, but accepts most other
                forms of payment.
              </Fragment>
            ) : (
              <Fragment>
                {location.displayNamePrimary} accepts many major insurance plans and multiple forms
                of payment, making it easier to get the affordable care you need. It is always a
                good idea to contact your insurance company before your visit to ensure the{' '}
                {this.entity} is in network for the service(s) you're seeking.
              </Fragment>
            )}
          </Copy>
          <AcceptedContainer>
            <Accepted>
              <InsuranceIcon src="/images/icons/Icon-BusinessHours.svg" alt="Business hours icon" />
              <AcceptedInfo>
                <AcceptedHeader>Accepted Insurance Plans</AcceptedHeader>
                <AcceptedInsurances>
                  <Collapse in={!insuranceSectionCollapsed} timeout="auto">
                    {collapsedInsurances.map((insurance, i) => (
                      <InsuranceType key={i}>{insurance}</InsuranceType>
                    ))}
                  </Collapse>
                  <Collapse in={insuranceSectionCollapsed} timeout="auto">
                    {this.insurances.map((insurance, i) => (
                      <InsuranceType key={i}>{insurance}</InsuranceType>
                    ))}
                  </Collapse>
                  {this.shouldShowViewAllCta() && (
                    <ViewAll onClick={this.toggleViewAllInsuranceProviders}>
                      {insuranceSectionCollapsed
                        ? 'Show less insurance plans...'
                        : 'View all insurance plans...'}
                    </ViewAll>
                  )}
                </AcceptedInsurances>
              </AcceptedInfo>
            </Accepted>
            {!isEmptyArray(acceptedPayments) && (
              <Accepted>
                <InsuranceIcon src="/images/icons/dollar-sign.svg" alt="Dollar sign icon" />
                <AcceptedInfo>
                  <AcceptedHeader>Accepted Payments</AcceptedHeader>
                  <AcceptedPayments>
                    {acceptedPayments.map((payment, i) => (
                      <PaymentType key={i}>{payment}</PaymentType>
                    ))}
                  </AcceptedPayments>
                </AcceptedInfo>
              </Accepted>
            )}
          </AcceptedContainer>
        </Root>
      </PanelCard>
    );
  };
}
